import React, { useEffect, useState } from 'react';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import Button from '@/components/ui/Button';
import LoadingState from '@/components/ui/LoadingState';
import useLockBodyScroll from '@/hooks/useLockBodyScroll';
import CalendarView from '@restaurant-manager/frontend/absence-management/time-off-management/components/CalendarView';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const EmployeeDetailPanel = ({ employee, onClose, onMessage, onCall }) => {
  useLockBodyScroll();

  const { t, language } = useTranslation(['employeeDirectory', 'common']);

  const [activeTab, setActiveTab] = useState('overview');
  const [absenceView, setAbsenceView] = useState('list');
  const [isOpening, setIsOpening] = useState(true);

  useEffect(() => {
    setAbsenceView('list');
  }, [employee?.id]);

  useEffect(() => {
    setIsOpening(true);
    const timeoutId = window.setTimeout(() => setIsOpening(false), 250);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [employee?.id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (!employee) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'on-work':
        return 'bg-success';
      case 'off-work':
        return 'bg-muted-foreground';
      case 'in-vacation':
        return 'bg-warning';
      case 'holiday':
        return 'bg-primary';
      case 'sick-leave':
        return 'bg-error';
      default:
        return 'bg-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'on-work':
        return t('employeeDirectory:statuses.onWork', 'On Work');
      case 'off-work':
        return t('employeeDirectory:statuses.offWork', 'Off Work');
      case 'in-vacation':
        return t('employeeDirectory:statuses.inVacation', 'In Vacation');
      case 'holiday':
        return t('employeeDirectory:statuses.holiday', 'Holiday');
      case 'sick-leave':
        return t('employeeDirectory:statuses.sickLeave', 'Sick Leave');
      default:
        return t('employeeDirectory:statuses.unknown', 'Unknown');
    }
  };

  const getAbsenceStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return {
          label: t(
            'employeeDirectory:detail.absences.statuses.approved',
            'Approved'
          ),
          className: 'bg-success/10 text-success border border-success/30',
        };
      case 'pending':
        return {
          label: t(
            'employeeDirectory:detail.absences.statuses.pending',
            'Pending'
          ),
          className: 'bg-warning/10 text-warning border border-warning/30',
        };
      case 'rejected':
        return {
          label: t(
            'employeeDirectory:detail.absences.statuses.declined',
            'Declined'
          ),
          className: 'bg-error/10 text-error border border-error/30',
        };
      case 'cancelled':
      case 'canceled':
        return {
          label: t(
            'employeeDirectory:detail.absences.statuses.cancelled',
            'Cancelled'
          ),
          className: 'bg-muted text-muted-foreground border border-border',
        };
      default:
        return {
          label: status
            ? status.charAt(0).toUpperCase() + status.slice(1)
            : t('employeeDirectory:statuses.unknown', 'Unknown'),
          className: 'bg-muted text-muted-foreground border border-border',
        };
    }
  };

  const parseDateFromInput = (value) => {
    if (!value || typeof value !== 'string') {
      return null;
    }
    const parts = value
      .split('-')
      .map((segment) => Number.parseInt(segment, 10));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      const fallback = new Date(value);
      return Number.isNaN(fallback?.getTime?.()) ? null : fallback;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  };

  const formatAbsenceRange = (start, end) => {
    const locale = language || 'en';
    const formatter = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const startDate = parseDateFromInput(start);
    const endDate = parseDateFromInput(end);

    if (startDate && endDate) {
      if (startDate.getTime() === endDate.getTime()) {
        return formatter.format(startDate);
      }
      return `${formatter.format(startDate)} – ${formatter.format(endDate)}`;
    }

    if (startDate) {
      return formatter.format(startDate);
    }

    if (endDate) {
      return formatter.format(endDate);
    }

    return t(
      'employeeDirectory:detail.absences.dateUnavailable',
      'Date not available'
    );
  };

  const tabs = [
    {
      id: 'overview',
      label: t('employeeDirectory:detail.tabs.overview', 'Overview'),
      icon: 'User',
    },
    {
      id: 'absences',
      label: t('employeeDirectory:detail.tabs.absences', 'Absences'),
      icon: 'Calendar',
    },
    {
      id: 'documents',
      label: t('employeeDirectory:detail.tabs.documents', 'Documents'),
      icon: 'FileText',
    },
  ];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Basic Info */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          {t(
            'employeeDirectory:detail.overview.basicInfo',
            'Basic Information'
          )}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('employeeDirectory:detail.overview.employeeId', 'Employee ID')}
            </label>
            <p className="text-sm text-foreground">{employee?.employeeId}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('employeeDirectory:detail.overview.hireDate', 'Hire Date')}
            </label>
            <p className="text-sm text-foreground">{employee?.hireDate}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('employeeDirectory:detail.overview.department', 'Department')}
            </label>
            <p className="text-sm text-foreground">{employee?.department}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t(
                'employeeDirectory:detail.overview.shiftPreference',
                'Shift Preference'
              )}
            </label>
            <p className="text-sm text-foreground">
              {employee?.shiftPreference}
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">
              {t(
                'employeeDirectory:detail.overview.assignedLocations',
                'Assigned Locations'
              )}
            </label>
            {Array.isArray(employee?.locations) &&
            employee.locations.length > 0 ? (
              <div className="flex flex-wrap gap-6 mt-3 ">
                {employee.locations.map((assignment, index) => (
                  <span
                    key={
                      assignment?.id ??
                      `${assignment?.name ?? 'location'}-${index}`
                    }
                    className="inline-flex items-center gap-2 bg-muted/60 text-muted-foreground px-3 py-1 rounded-full text-sm"
                  >
                    <Icon name="MapPin" size={14} />
                    <span className="font-medium text-foreground">
                      {assignment?.name ??
                        t(
                          'employeeDirectory:detail.overview.unnamedLocation',
                          'Unnamed Location'
                        )}
                    </span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                {t(
                  'employeeDirectory:detail.overview.noLocations',
                  'No locations assigned'
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Primary Contact */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          {t('employeeDirectory:detail.contact.primary', 'Primary Contact')}
        </h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Icon name="Phone" size={16} className="text-muted-foreground" />
            <div>
              <p className="text-sm text-foreground">{employee?.phone}</p>
              <p className="text-xs text-muted-foreground">
                {t('employeeDirectory:detail.contact.labels.mobile', 'Mobile')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={16} className="text-muted-foreground" />
            <div>
              <p className="text-sm text-foreground">{employee?.email}</p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'employeeDirectory:detail.contact.labels.workEmail',
                  'Work Email'
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <div>
              <p className="text-sm text-foreground">{employee?.address}</p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'employeeDirectory:detail.contact.labels.homeAddress',
                  'Home Address'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          {t('employeeDirectory:detail.contact.emergency', 'Emergency Contact')}
        </h4>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('employeeDirectory:detail.contact.labels.name', 'Name')}
            </label>
            <p className="text-sm text-foreground">
              {employee?.emergencyContact?.name}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t(
                'employeeDirectory:detail.contact.labels.relationship',
                'Relationship'
              )}
            </label>
            <p className="text-sm text-foreground">
              {employee?.emergencyContact?.relationship}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {t('employeeDirectory:detail.contact.labels.phone', 'Phone')}
            </label>
            <p className="text-sm text-foreground">
              {employee?.emergencyContact?.phone}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAbsencesTab = () => {
    const absences = Array.isArray(employee?.absences) ? employee.absences : [];
    const isLoading = Boolean(employee?.absencesLoading);
    const error = employee?.absencesError;
    const sortedAbsences = absences.slice().sort((a, b) => {
      const startA =
        parseDateFromInput(a?.startDate) ??
        parseDateFromInput(a?.endDate) ??
        new Date(0);
      const startB =
        parseDateFromInput(b?.startDate) ??
        parseDateFromInput(b?.endDate) ??
        new Date(0);
      return startB?.getTime?.() - startA?.getTime?.();
    });

    const hasAbsences = sortedAbsences.length > 0;
    const errorMessage =
      (error && (error.message || error?.response?.data?.message)) ||
      t(
        'employeeDirectory:detail.absences.errorFallback',
        'Unable to load absences. Please try again.'
      );

    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-foreground">
              {t(
                'employeeDirectory:detail.absences.historyTitle',
                'Absence History'
              )}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t(
                'employeeDirectory:detail.absences.historyDescription',
                'Review approved and pending absences for this employee.'
              )}
            </p>
          </div>
          <div className="inline-flex items-center gap-2">
            <Button
              variant={absenceView === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAbsenceView('list')}
              iconName="List"
            >
              {t('employeeDirectory:detail.absences.listView', 'List')}
            </Button>
            <Button
              variant={absenceView === 'calendar' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAbsenceView('calendar')}
              iconName="Calendar"
            >
              {t('employeeDirectory:detail.absences.calendarView', 'Calendar')}
            </Button>
          </div>
        </div>

        {isLoading && (
          <LoadingState
            message={t(
              'employeeDirectory:detail.absences.loading',
              'Loading absence data…'
            )}
            heightClassName="min-h-[6rem]"
            className="py-12"
          />
        )}

        {!isLoading && error && (
          <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-lg text-sm text-destructive-foreground">
            {errorMessage}
          </div>
        )}

        {!isLoading &&
          !error &&
          absenceView === 'list' &&
          (hasAbsences ? (
            <div className="space-y-3">
              {sortedAbsences?.map((absence) => {
                const badge = getAbsenceStatusBadge(absence?.status);
                const durationLabel =
                  typeof absence?.duration === 'number' && absence.duration > 0
                    ? formatTranslation(
                        t(
                          `employeeDirectory:detail.absences.duration.${
                            absence.duration > 1 ? 'plural' : 'singular'
                          }`,
                          absence.duration > 1
                            ? '{{count}} days'
                            : '{{count}} day'
                        ),
                        { count: absence.duration }
                      )
                    : null;
                const typeLabel = absence?.type
                  ? absence.type.replace(/-/g, ' ')
                  : t(
                      'employeeDirectory:detail.absences.typeFallback',
                      'Absence'
                    );

                return (
                  <div
                    key={absence?.id}
                    className="p-4 border border-border rounded-lg bg-muted/30"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground capitalize">
                          {typeLabel}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatAbsenceRange(
                            absence?.startDate,
                            absence?.endDate
                          )}
                          {durationLabel ? ` • ${durationLabel}` : ''}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge?.className}`}
                      >
                        {badge?.label}
                      </span>
                    </div>
                    {absence?.reason && (
                      <p className="text-sm text-muted-foreground mt-3">
                        {absence.reason}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-12 border border-border rounded-lg bg-muted/20">
              <Icon
                name="Calendar"
                size={24}
                className="text-muted-foreground mb-2"
              />
              <p className="text-sm text-muted-foreground">
                {t(
                  'employeeDirectory:detail.absences.emptyList',
                  'No absences recorded for this employee yet.'
                )}
              </p>
            </div>
          ))}

        {!isLoading &&
          !error &&
          absenceView === 'calendar' &&
          (hasAbsences ? (
            <CalendarView timeOffData={sortedAbsences} />
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-12 border border-border rounded-lg bg-muted/20">
              <Icon
                name="Calendar"
                size={24}
                className="text-muted-foreground mb-2"
              />
              <p className="text-sm text-muted-foreground">
                {t(
                  'employeeDirectory:detail.absences.emptyCalendar',
                  'No absences to display on the calendar.'
                )}
              </p>
            </div>
          ))}
      </div>
    );
  };

  const renderDocumentsTab = () => (
    <div className="space-y-6">
      {/* Document Categories */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          {t('employeeDirectory:detail.documents.title', 'Employee Documents')}
        </h4>
        <div className="space-y-3">
          {employee?.documents?.map((doc, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <Icon
                  name="FileText"
                  size={16}
                  className="text-muted-foreground"
                />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {doc?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {doc?.type} • {doc?.size}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!doc?.previewUrl}
                  onClick={() => {
                    if (doc?.previewUrl) {
                      window.open(
                        doc.previewUrl,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }
                  }}
                  aria-label={formatTranslation(
                    t(
                      'employeeDirectory:detail.documents.preview',
                      'Preview {{name}}'
                    ),
                    { name: doc?.name ?? '' }
                  )}
                >
                  <Icon name="Eye" size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!doc?.downloadUrl}
                  onClick={() => {
                    if (doc?.downloadUrl) {
                      window.open(
                        doc.downloadUrl,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }
                  }}
                  aria-label={formatTranslation(
                    t(
                      'employeeDirectory:detail.documents.download',
                      'Download {{name}}'
                    ),
                    { name: doc?.name ?? '' }
                  )}
                >
                  <Icon name="Download" size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upload New Document */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          {t(
            'employeeDirectory:detail.documents.uploadTitle',
            'Upload New Document'
          )}
        </h4>
        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
          <Icon
            name="Upload"
            size={24}
            className="text-muted-foreground mx-auto mb-2"
          />
          <p className="text-sm text-muted-foreground mb-2">
            {t(
              'employeeDirectory:detail.documents.uploadHint',
              'Drag and drop files here, or click to browse'
            )}
          </p>
          <Button variant="outline" size="sm">
            {t(
              'employeeDirectory:detail.documents.chooseFiles',
              'Choose Files'
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center p-0 sm:items-center sm:p-4">
      <div className="bg-card border border-border rounded-none sm:rounded-lg shadow-elevation-2 w-full max-w-4xl h-full max-h-screen sm:h-[85vh] sm:max-h-[85vh] overflow-hidden flex flex-col relative">
        {isOpening && (
          <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-sm">
            <LoadingState
              message={t(
                'employeeDirectory:detail.loading',
                'Loading employee details…'
              )}
              heightClassName="h-full"
              spinnerSize="h-10 w-10"
            />
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col gap-4 p-4 border-b border-border shrink-0 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="flex items-start space-x-4 sm:items-center">
            <div className="relative">
              <EmployeeAvatar
                url={employee?.avatarUrl}
                name={employee?.name}
                alt={formatTranslation(
                  t(
                    'employeeDirectory:detail.avatarAlt',
                    '{{name}} profile photo'
                  ),
                  {
                    name:
                      employee?.name ??
                      t('employeeDirectory:card.unknownName', 'Employee'),
                  }
                )}
                status={employee?.status}
                size="lg"
                wrapperClassName="border border-border"
              />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-semibold text-foreground">
                  {employee?.name}
                </h2>
                {employee?.isManager && (
                  <Icon name="Crown" size={18} className="text-accent" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {employee?.role} • {employee?.department}
              </p>
              <div className="flex items-center space-x-1 mt-1">
                <div
                  className={`w-2 h-2 ${getStatusColor(
                    employee?.status
                  )} rounded-full`}
                ></div>
                <span className="text-sm text-muted-foreground">
                  {getStatusText(employee?.status)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => onMessage(employee)}>
              <Icon name="MessageSquare" size={16} className="mr-2" />
              {t('employeeDirectory:detail.actions.message', 'Message')}
            </Button>
            <Button variant="outline" onClick={() => onCall(employee)}>
              <Icon name="Phone" size={16} className="mr-2" />
              {t('employeeDirectory:detail.actions.call', 'Call')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border shrink-0">
          <nav className="flex space-x-4 overflow-x-auto px-4 sm:space-x-8 sm:px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="text-sm font-medium">{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div
          className={`p-4 overflow-y-auto flex-1 min-h-0 transition-opacity duration-200 sm:p-6 ${
            isOpening
              ? 'pointer-events-none select-none opacity-0'
              : 'opacity-100'
          }`}
        >
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'absences' && renderAbsencesTab()}
          {activeTab === 'documents' && renderDocumentsTab()}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailPanel;
